import React, { useState } from "react";
import "../styles/Profile.css";
import { SKILLMASTER_API } from "./config";

const readStoredUser = () => {
  const fullName = (localStorage.getItem("userName") || "").trim();
  const [firstName = "", ...rest] = fullName.split(/\s+/);
  return {
    firstName,
    lastName: rest.join(" "),
    email: localStorage.getItem("userEmail") || "",
    membership: localStorage.getItem("userMembership") || "Free",
  };
};

const getInitials = (first, last) =>
  ((first || "").charAt(0) + (last || "").charAt(0)).toUpperCase() || "U";

const formatMembership = (m) => {
  if (!m) return "Free Member";
  if (m.toLowerCase() === "superadmin") return "Super Admin";
  return `${m.charAt(0).toUpperCase()}${m.slice(1)} Member`;
};

const Profile = () => {
  const storedUser = readStoredUser();
  const [activeTab, setActiveTab] = useState("Profile");
  const [savedData, setSavedData] = useState({
    firstName: storedUser.firstName,
    lastName: storedUser.lastName,
    headline: localStorage.getItem("userHeadline") || "",
  });
  const [formData, setFormData] = useState(savedData);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  const menuItems = [
    { name: "Profile" },
    { name: "Photo" },
    { name: "Account Security" },
    { name: "Subscriptions" },
    { name: "Payment methods" },
    { name: "Privacy" },
  ];

  const filledFields = [formData.firstName, formData.lastName, formData.headline].filter(
    (v) => v && v.trim()
  ).length;
  const completion = Math.round((filledFields / 3) * 100);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setStatus(null);
  };

  const handleCancel = () => {
    setFormData(savedData);
    setStatus(null);
  };

  const handleSave = async () => {
    if (!formData.firstName.trim()) {
      setStatus({ type: "error", text: "First name is required." });
      return;
    }
    setSaving(true);
    setStatus(null);
    localStorage.setItem("userHeadline", formData.headline.trim());

    try {
      const response = await fetch(`${SKILLMASTER_API}/user/update-profile`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          email: storedUser.email,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
        }),
      });
      const data = await response.json();

      if (response.ok && data.success) {
        const next = {
          firstName: data.user?.firstName ?? formData.firstName.trim(),
          lastName: data.user?.lastName ?? formData.lastName.trim(),
          headline: formData.headline.trim(),
        };
        localStorage.setItem("userName", `${next.firstName} ${next.lastName}`.trim());
        setSavedData(next);
        setFormData(next);
        setStatus({ type: "success", text: "Profile updated successfully." });
      } else {
        setStatus({ type: "error", text: data.message || "Could not update profile." });
      }
    } catch (err) {
      setStatus({ type: "error", text: "Network error. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  const displayName = `${savedData.firstName} ${savedData.lastName}`.trim() || "User";

  return (
    <div className="profile-page-wrapper">
      <div className="profile-main-container">
        <div className="profile-sidebar">
          <div className="profile-user-header">
            <div className="profile-avatar-container">
              <span className="profile-user-initials">
                {getInitials(savedData.firstName, savedData.lastName)}
              </span>
              <div className="profile-online-status"></div>
            </div>
            <div className="profile-user-details">
              <h3 className="profile-username">{displayName}</h3>
              <p className="profile-member-status">{formatMembership(storedUser.membership)}</p>
            </div>
          </div>

          <nav className="profile-navigation">
            <ul className="profile-menu-list">
              {menuItems.map((item) => (
                <li
                  key={item.name}
                  className={`profile-menu-item ${activeTab === item.name ? "profile-menu-active" : ""}`}
                  onClick={() => setActiveTab(item.name)}
                >
                  <span className="profile-menu-label">{item.name}</span>
                  <div className="profile-menu-indicator"></div>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="profile-content-area">
          <div className="profile-header-section">
            <div className="profile-title-group">
              <h1>Public Profile</h1>
              <p>Customize your profile</p>
            </div>
            <div className="profile-progress-widget">
              <div className="profile-progress-ring">
                <span>{completion}%</span>
              </div>
              <small>Profile Complete</small>
            </div>
          </div>

          <div className="profile-form-container">
            <div className="profile-form-header">
              <h2>Basic Information</h2>
              <p>Displayed on your public profile</p>
            </div>

            <div className="profile-form-body">
              <div className="profile-form-row">
                <div className="profile-input-group">
                  <label htmlFor="profile-first-name">First Name</label>
                  <input
                    id="profile-first-name"
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange("firstName", e.target.value)}
                    className="profile-text-input"
                  />
                </div>
                <div className="profile-input-group">
                  <label htmlFor="profile-last-name">Last Name</label>
                  <input
                    id="profile-last-name"
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange("lastName", e.target.value)}
                    className="profile-text-input"
                  />
                </div>
              </div>

              <div className="profile-input-group">
                <label htmlFor="profile-headline">Professional Headline</label>
                <input
                  id="profile-headline"
                  type="text"
                  placeholder="e.g., Senior Software Developer"
                  value={formData.headline}
                  onChange={(e) => handleInputChange("headline", e.target.value)}
                  maxLength="60"
                  className="profile-text-input"
                />
                <small className="profile-input-help">
                  {60 - formData.headline.length} characters remaining
                </small>
              </div>

              {status && (
                <p
                  className="profile-input-help"
                  style={{ color: status.type === "success" ? "#15803d" : "#dc2626", fontWeight: 600 }}
                >
                  {status.text}
                </p>
              )}
            </div>

            <div className="profile-form-actions">
              <button type="button" className="profile-btn-cancel" onClick={handleCancel} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="profile-btn-save" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
